// Pure decision for what to do when a signed-in user lands on a first path segment that is
// NOT one of their workspaces (SCD-936). Kept pure + standalone so it's unit-testable and the
// "not found vs no access vs loop" logic lives in exactly one place.
//
// - 'enter'      — it's the user's own workspace; proceed normally.
// - 'no-access'  — the workspace EXISTS but the user isn't a member → /no-access screen (#3+5).
// - 'not-found'  — the workspace does NOT exist → not-found / 404 (#7). NOT /no-access.
// - 'standalone' — a reserved in-place screen route (/no-access) → render it, never redirect
//                  (prevents the /no-access?next=/no-access self-redirect loop).
// - 'default'    — root or /auth → fall back to the default-workspace / picker logic.
export type WorkspaceEntryOutcome = 'enter' | 'no-access' | 'not-found' | 'standalone' | 'default'

export function resolveWorkspaceEntry(args: {
    slug: string
    userBelongs: boolean
    workspaceExists: boolean
}): WorkspaceEntryOutcome {
    const { slug, userBelongs, workspaceExists } = args
    if (userBelongs) return 'enter'
    if (slug === '' || slug === 'auth') return 'default'
    // /no-access is its own in-place screen — it must never be treated as an inaccessible
    // workspace, or loadUserData would redirect it back to /no-access forever.
    if (slug === 'no-access') return 'standalone'
    return workspaceExists ? 'no-access' : 'not-found'
}

// The no-access screen redirects with a `next` link to return to after re-auth. Never let that
// point back at /no-access (self-loop) or /auth — fall back to the root in those cases.
export function sanitizeNext(next: string | null | undefined): string {
    if (!next || next.startsWith('/no-access') || next.startsWith('/auth')) return '/'
    return next
}
